"use client";

import moneyIcon from "@/assets/icons/money-icon.webp";
import NotFound from "@/assets/images/not-found.png";
import CustomTooltip from "@/components/CustomTooltip";
import { Info } from "@phosphor-icons/react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";

export default function BankingPenetration({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
}) {
  const searchParams = useSearchParams();
  const selectedCountryId = searchParams.get("country");

  // Get the title with country name if country is selected, otherwise region name
  const getTitle = () => {
    if (selectedCountryName) {
      return `Banking penetration — ${selectedCountryName}`;
    }
    return `Banking penetration — By Country`;
  };

  const totalBars = selectedCountryId ? 40 : 25; // density of bars

  // Generate countries data from World Bank API data
  const generateCountriesData = () => {
    if (regionData && Array.isArray(regionData)) {
      // Region view - use real World Bank banking data
      return regionData
        .filter((country) => country.macroeconomic?.banking > 0)
        .sort(
          (a, b) =>
            (b.macroeconomic?.banking || 0) - (a.macroeconomic?.banking || 0),
        )
        .slice(0, 5) // Take top 5 countries
        .map((country) => ({
          name: country.country_name || "Unknown",
          percentage: Math.round(country.macroeconomic.banking),
        }));
    } else {
      // No data available
      return [];
    }
  };

  const filtered = generateCountriesData();

  // Render Stat Card for Single Country
  if (selectedCountryId) {
    const percentage = countryData?.macroeconomic?.banking
      ? Math.round(countryData.macroeconomic.banking)
      : 0;
    const hasData = countryData?.macroeconomic?.banking > 0;

    // For banking card, let's use a similar radial progress or just a big number.
    // Given the task "Country View: Stat card", reusing the style from Internet Penetration (Radial) is consistent.

    return (
      <div className='flex flex-col h-full'>
        <div className='flex items-center justify-between gap-2 mb-4'>
          <p className='text-base font-medium sm:text-sm'>{getTitle()}</p>
          <CustomTooltip
            trigger={
              <span className='-mt-2 -mr-2 text-white/40'>
                <Info className='size-5' />
              </span>
            }
            content='Account ownership at a financial institution or with a mobile-money-service provider (% of population ages 15+), sourced from the World Bank and using the latest available year (which may vary by country).'
          />
        </div>

        {hasData ? (
          <div className='flex items-center gap-3 flex-1 pb-3.5'>
            <div className='relative size-9'>
              <Image src={moneyIcon.src} layout='fill' alt='Total Funding' />
            </div>
            <span className='text-3xl font-bold'>{percentage}%</span>
          </div>
        ) : (
          <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
            <div className='relative block mx-auto aspect-square size-20'>
              <Image
                objectFit='cover'
                src={NotFound.src}
                alt='NotFound Image'
                layout='fill'
                className='object-cover'
              />
            </div>
            <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
            <p className='mt-1 text-xs text-white/40'>
              No banking data available
            </p>
          </div>
        )}
        <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
          Source: World Bank
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Display title with region/country name */}
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-base font-medium sm:text-sm'>{getTitle()}</h3>
        <CustomTooltip
          trigger={
            <span className='-mt-2 -mr-2 text-white/40'>
              <Info className='size-5' />
            </span>
          }
          content='Account ownership at a financial institution or with a mobile-money-service provider (% of population ages 15+), sourced from the World Bank and using the latest available year (which may vary by country).'
        />
      </div>

      {filtered.length > 0 ? (
        <div className='space-y-3.5'>
          {filtered.map(({ name, percentage }) => {
            const filledBars = Math.round((percentage / 100) * totalBars);
            const bars = Array.from({ length: totalBars });

            return (
              <div
                key={name}
                className='flex items-center gap-4'
                title={`${name} • ${percentage}% • Latest Year`}
              >
                {/* hide label if a single country is selected */}
                {!selectedCountryId && (
                  <p className='w-20 text-xs font-medium opacity-60'>{name}</p>
                )}

                <div className='flex flex-1 gap-[3px]'>
                  {bars.map((_, index) => (
                    <div
                      key={index}
                      className={`flex-1 h-6 rounded-sm ${
                        index < filledBars ? "bg-green-500" : "bg-white/10"
                      }`}
                    />
                  ))}
                </div>

                <p className='w-5 text-xs font-medium text-right opacity-50'>
                  {percentage}%
                </p>
              </div>
            );
          })}
          <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
            Source: World Bank
          </div>
        </div>
      ) : (
        <div className='p-6 py-4 text-center border border-dashed rounded-lg'>
          <div className='relative block mx-auto aspect-square size-20'>
            <Image
              objectFit='cover'
              src={NotFound.src}
              alt='NotFound Image'
              layout='fill'
              className='object-cover'
            />
          </div>
          <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
          <p className='mt-1 text-xs text-white/40'>
            No World Bank banking data available for this selection.
          </p>
        </div>
      )}
    </>
  );
}
